<template>
  <div v-if="isLoading" class="pl-stage" :class="{ 'pl-loaded': isLoaded }">

    <div class="pl-center">
      <!-- Logo -->
      <div class="pl-logo-wrap">
        <img src="/images/assets/logo/RPD_logomark_type_white.png" alt="Real Property Data" class="pl-logo" />
      </div>

      <!-- Eyebrow -->
      <div class="pl-eyebrow">
        <span class="pl-eyebrow-bar"></span>
        Loading workspace
      </div>

      <!-- Headline -->
      <h1 class="pl-headline">Real-time property data,<br>in one place.</h1>

      <!-- Progress -->
      <div class="pl-bar-wrap">
        <div class="pl-bar-track">
          <div class="pl-bar-fill" :style="{ width: displayPct + '%' }"></div>
        </div>
        <div class="pl-bar-row">
          <span class="pl-status">{{ currentStatus }}…</span>
          <span class="pl-pct">{{ String(displayPct).padStart(2, '0') }}%</span>
        </div>
      </div>
    </div>

    <footer class="pl-footer">
      <span>RPD · v4</span>
      <span class="pl-dot"></span>
      <span>info@realpropertydata.ca</span>
    </footer>

  </div>
</template>

<script>
import { useRequestCounter } from "../../store/RequestCounter";

const STATUS_MESSAGES = [
  "Connecting to listings server",
  "Pulling live valuations",
  "Indexing 142,308 properties",
  "Computing starting rates",
  "Syncing active projects",
  "Refreshing comparables",
  "Calibrating market signals",
  "Almost there",
];

export default {
  name: "PageLoader",

  data() {
    return {
      isLoading: true,
      isLoaded: false,
      store: useRequestCounter(),
      statusIndex: 0,
      animPct: 0,
      _statusTimer: null,
      _raf: null,
      _animStart: null,
    };
  },

  computed: {
    pct() {
      const { requestLength, completedRequest } = this.store.reqCounter;
      if (!requestLength) return 0;
      return Math.min(100, Math.round((completedRequest / requestLength) * 100));
    },
    // Use real progress when available, otherwise show the looping animation
    displayPct() {
      return this.pct > 0 ? this.pct : this.animPct;
    },
    currentStatus() {
      return STATUS_MESSAGES[this.statusIndex];
    },
    getCounter() {
      return this.store.getCounter;
    },
  },

  watch: {
    getCounter(newVal) {
      const { requestLength } = this.store.reqCounter;
      if (newVal >= requestLength && requestLength > 0) {
        setTimeout(() => {
          this.isLoaded = true;
          document.documentElement.classList.remove("loading");
          setTimeout(() => { this.isLoading = false; }, 550);
        }, 300);
      }
    },
  },

  created() {
    document.documentElement.classList.add("loading");
  },

  mounted() {
    // Cycle status messages
    this._statusTimer = setInterval(() => {
      this.statusIndex = (this.statusIndex + 1) % STATUS_MESSAGES.length;
    }, 1400);

    // Looping eased progress animation (visible when real progress = 0)
    this._animStart = performance.now();
    const loop = (t) => {
      const elapsed = (t - this._animStart) / 1000;
      const cycle = (elapsed % 4) / 4;
      const eased = 1 - Math.pow(1 - cycle, 2.2);
      this.animPct = Math.round(eased * 100);
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  },

  beforeDestroy() {
    if (this._statusTimer) clearInterval(this._statusTimer);
    if (this._raf) cancelAnimationFrame(this._raf);
  },
};
</script>

<style scoped>
/* ── Stage ── */
.pl-stage {
  position: fixed;
  inset: 0;
  z-index: 999999999;
  background: #12283F;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.5s cubic-bezier(0.2, 0, 0, 1);
  -webkit-font-smoothing: antialiased;
}

.pl-loaded {
  opacity: 0;
  pointer-events: none;
}

/* ── Center block ── */
.pl-center {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: min(720px, calc(100% - 96px));
  z-index: 2;
}

/* ── Logo ── */
.pl-logo-wrap {
  margin-bottom: 56px;
}

.pl-logo {
  width: 260px;
  height: auto;
  display: block;
}

/* ── Eyebrow ── */
.pl-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: #FC5868;
}

.pl-eyebrow-bar {
  width: 28px;
  height: 3px;
  background: #FC5868;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

/* ── Headline ── */
.pl-headline {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: clamp(36px, 4.6vw, 56px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: #ffffff;
  margin: 20px 0 40px;
  max-width: 640px;
}

/* ── Progress bar ── */
.pl-bar-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pl-bar-track {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.10);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.pl-bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: #FC5868;
  border-radius: 999px;
  transition: width 80ms linear;
  box-shadow: 0 0 24px rgba(252, 88, 104, 0.55);
}

.pl-bar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pl-status {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Consolas, monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.65);
  text-transform: lowercase;
}

.pl-pct {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Consolas, monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  color: #FC5868;
  font-weight: 500;
}

/* ── Footer ── */
.pl-footer {
  position: absolute;
  left: 48px;
  right: 48px;
  bottom: 32px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: 'JetBrains Mono', 'SFMono-Regular', Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
}

.pl-dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.42);
  display: inline-block;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .pl-center {
    width: calc(100% - 48px);
  }

  .pl-logo {
    width: 180px;
  }

  .pl-logo-wrap {
    margin-bottom: 40px;
  }

  .pl-headline {
    margin: 16px 0 32px;
    font-size: 32px;
  }

  .pl-footer {
    left: 24px;
    right: 24px;
    bottom: 24px;
    gap: 10px;
  }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .pl-stage {
    transition: none !important;
  }

  .pl-bar-fill {
    transition: none !important;
    box-shadow: none !important;
  }
}
</style>
